/**
 * Collection writes (create / rename / delete / add sounds / remove a sound). Every success invalidates the whole
 * collections prefix, so both the list (counts, cover mosaics) and any open collection refetch.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AudioStudioApiError, audioStudioApi } from '@/api/audioStudioApi';
import { audioKeys } from './queries';

export const NAME_TAKEN = 'That name already exists';
export const COLLECTION_NAME_MAX = 60;

/** 409 (name already used, case-insensitive) reads as a plain sentence; anything else keeps the service's message. */
export function collectionErrorMessage(e: unknown): string {
  if (e instanceof AudioStudioApiError && e.status === 409) return NAME_TAKEN;
  return (e as Error)?.message || 'Something went wrong';
}

function useInvalidateCollections() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: audioKeys.collectionsAll });
}

export function useCreateCollection() {
  const invalidate = useInvalidateCollections();
  return useMutation({
    mutationFn: (body: { name: string; track_ids?: string[]; brand_id?: string }) => audioStudioApi.createCollection(body),
    onSuccess: () => { void invalidate(); },
  });
}

/** Rename (`name`) and / or change the brand (`brand_id`, "" = no brand). */
export function useUpdateCollection() {
  const invalidate = useInvalidateCollections();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; name?: string; brand_id?: string }) => audioStudioApi.updateCollection(id, patch),
    onSuccess: () => { void invalidate(); },
  });
}

export function useDeleteCollection() {
  const invalidate = useInvalidateCollections();
  return useMutation({
    mutationFn: (id: string) => audioStudioApi.deleteCollection(id),
    onSuccess: () => { void invalidate(); },
  });
}

/** Adds one sound to, or removes it from, a collection. */
export function useCollectionMembership() {
  const invalidate = useInvalidateCollections();
  return useMutation({
    mutationFn: ({ collectionId, trackId, add }: { collectionId: string; trackId: string; add: boolean }) =>
      add ? audioStudioApi.addToCollection(collectionId, [trackId]) : audioStudioApi.removeFromCollection(collectionId, trackId),
    onSuccess: () => { void invalidate(); },
  });
}
